import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { formatMoney } from '../utils/formatMoney';

const Activity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('today'); // 'all', 'today', 'yesterday', 'week', 'month', 'year', 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchActivities();
  }, [filter, startDate, endDate]);

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filter) params.append('filter', filter);
      if (filter === 'custom' && startDate) params.append('startDate', startDate);
      if (filter === 'custom' && endDate) params.append('endDate', endDate);

      const response = await axios.get(
        `${API_URL}/api/activities?${params.toString()}`
      );
      setActivities(response.data.activities);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const activityDate = new Date(date);
    const diffInSeconds = Math.floor((now - activityDate) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return activityDate.toLocaleDateString();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityDetails = (activity) => {
    if (activity.activityType === 'transfer') {
      const metadata = activity.metadata || {};
      return {
        title: `Transfer: ${metadata.fromFileNo || 'N/A'} → ${metadata.toFileNo || 'N/A'}`,
        subtitle: `Amount: ${metadata.amount ? formatMoney(metadata.amount) : 'N/A'}`,
      };
    } else if (activity.activityType === 'sheet_upload') {
      const metadata = activity.metadata || {};
      return {
        title: `Sheet Uploaded`,
        subtitle: activity.description || 'Sheet uploaded',
      };
    } else if (activity.activityType === 'sheet_edit') {
      const metadata = activity.metadata || {};
      return {
        title: `Sheet Edited`,
        subtitle: activity.description || 'Sheet edited',
      };
    } else if (activity.activityType === 'sheet_delete') {
      return {
        title: `Sheet Deleted`,
        subtitle: activity.description || 'Sheet deleted',
      };
    }
    return {
      title: activity.description || activity.action,
      subtitle: '',
    };
  };

  return (
    <div>
      <div className="card">
        <h2 style={{ marginBottom: '20px' }}>Activity Log</h2>
        <p className="text-muted" style={{ marginBottom: '20px', fontSize: '14px' }}>
          View all system activities including sheet uploads, edits, deletions, and transfers.
        </p>

        {error && (
          <div className="alert alert-error">{error}</div>
        )}

        {/* Filter Buttons */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
            <button type="button" onClick={() => setFilter('today')} className={`btn-toggle ${filter === 'today' ? 'is-active' : ''}`}>
              Today
            </button>
            <button type="button" onClick={() => setFilter('yesterday')} className={`btn-toggle ${filter === 'yesterday' ? 'is-active' : ''}`}>
              Yesterday
            </button>
            <button type="button" onClick={() => setFilter('week')} className={`btn-toggle ${filter === 'week' ? 'is-active' : ''}`}>
              This Week
            </button>
            <button type="button" onClick={() => setFilter('month')} className={`btn-toggle ${filter === 'month' ? 'is-active' : ''}`}>
              This Month
            </button>
            <button type="button" onClick={() => setFilter('year')} className={`btn-toggle ${filter === 'year' ? 'is-active' : ''}`}>
              This Year
            </button>
            <button type="button" onClick={() => setFilter('all')} className={`btn-toggle ${filter === 'all' ? 'is-active' : ''}`}>
              All Time
            </button>
            <button type="button" onClick={() => setFilter('custom')} className={`btn-toggle ${filter === 'custom' ? 'is-active' : ''}`}>
              Custom Range
            </button>
          </div>

          {filter === 'custom' && (
            <div className="activity-custom-range">
              <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                <label htmlFor="start-date">Start Date</label>
                <input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                <label htmlFor="end-date">End Date</label>
                <input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div className="text-muted" style={{ fontSize: '14px' }}>
            Showing {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
          </div>
          <button
            type="button"
            onClick={fetchActivities}
            className="btn btn-primary btn-small"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading activities...</div>}

      {!loading && activities.length === 0 && (
        <div className="card">
          <p className="text-muted" style={{ textAlign: 'center', padding: '40px' }}>
            No activities found for the selected period.
          </p>
        </div>
      )}

      {!loading && activities.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {activities.map((activity) => {
              const details = getActivityDetails(activity);
              return (
                <div
                  key={activity.id}
                  className="activity-feed-row"
                  style={{ borderLeft: `4px solid ${activity.color}` }}
                >
                  <div
                    className="activity-feed-icon"
                    style={{
                      backgroundColor: activity.color,
                      boxShadow: `0 1px 4px ${activity.color}55`,
                    }}
                  >
                    {activity.iconText || activity.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="text-body" style={{ fontWeight: '600', marginBottom: '4px', fontSize: '15px' }}>
                      {details.title}
                    </div>
                    {details.subtitle && (
                      <div className="text-muted" style={{ fontSize: '13px', marginBottom: '8px' }}>
                        {details.subtitle}
                      </div>
                    )}
                    <div className="text-muted" style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '12px' }}>
                      <span>{formatDate(activity.createdAt)}</span>
                      <span>•</span>
                      <span>{getTimeAgo(activity.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Activity;

